import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Modal,
  createTheme,
  ThemeProvider,
  Button,
  Icon,
  IconButton,
  SvgIcon,
} from "@mui/material";
import "@fontsource/manrope";
import { ReactComponent as JiraIcon } from "../../images/jira-logo.svg";
import { ReactComponent as GithubIcon } from "../../images/github-logo.svg";
import { ReactComponent as SlackIcon } from "../../images/slack-logo.svg";
import { ReactComponent as AzureIcon } from "../../images/azure-logo.svg";
import { ReactComponent as SystemIcon } from "../../images/system-logo.svg";
import { ReactComponent as TestrailIcon } from "../../images/testrail-logo.svg";
import { ReactComponent as qTestIcon } from "../../images/qtest-logo.svg";
import { ReactComponent as ConfluenceIcon } from "../../images/confluence-logo.svg";

const theme = createTheme({
  typography: {
    fontFamily: "Manrope, Arial, sans-serif",
  },
});

const apps = [
  { name: "Jira", icon: <SvgIcon sx={{height: "40px", width: "40px"}} component={JiraIcon} inheritViewBox /> },
  { name: "Azure", icon: <SvgIcon sx={{height: "40px", width: "40px"}} component={AzureIcon} inheritViewBox /> },
  { name: "Github", icon: <SvgIcon sx={{height: "40px", width: "40px"}} component={GithubIcon} inheritViewBox /> },
  { name: "qTest", icon: <SvgIcon sx={{height: "40px", width: "40px"}} component={qTestIcon} inheritViewBox /> },
  { name: "TestRail", icon: <SvgIcon sx={{height: "40px", width: "40px"}} component={TestrailIcon} inheritViewBox /> },
  { name: "Slack", icon: <SvgIcon sx={{height: "40px", width: "40px"}} component={SlackIcon} inheritViewBox /> },
  { name: "Confluence", icon: <SvgIcon sx={{height: "40px", width: "40px"}} component={ConfluenceIcon} inheritViewBox /> },
];

const SelectAppModal = ({
  setPageCloser,
  selectedApp,
  setSelectedApp,
  proceedingApp,
  setProceeedingApp,
  issueType,
  taskType = "Import", 
}) => {
  const [isModalOpen, setIsModalOpen] = useState(true);

  const getFilteredApps = (issueType, taskType) => {
    const importApps = {
      "User Stories": ["Jira", "Azure"],
      "Test Cases": ["Azure"],
      "Bugs": ["Jira", "Confluence", "Azure"]
    };

    const exportApps = {
      "User Stories": ["Jira", "Azure"],
      "Test Cases": ["qTest", "TestRail", "Azure"],
      "Bugs": ["Jira", "Confluence", "Azure"],
      "Business Process Guide": ["Confluence"],
    };

    return taskType === "Export" ? exportApps[issueType] || [] : importApps[issueType] || [];
  };

  const filteredApps = getFilteredApps(issueType, taskType);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setPageCloser(false);
  };

  const handleSelectApp = (appName) => {
    if (!filteredApps.includes(appName)) return;
    setSelectedApp(appName);
  };

  const handleALMSelectionProceeding = () => {
    setProceeedingApp(selectedApp);
  };

  const isAppAvailable = (appName) => {
    return filteredApps.includes(appName);
  };

  return (
    <ThemeProvider theme={theme}>
      <Modal open={isModalOpen} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            maxWidth: "600px",
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            p: 3,
            display: "flex",
            flexDirection: "column",
            outline: "none",
          }}
        >
          <Typography variant="h6" sx={{ mb: 4, fontSize: "16px" }}>
            Select Application
          </Typography>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            {apps.map((app) => {
              const isAvailable = isAppAvailable(app.name);
              const isSelected = selectedApp === app.name;
              
              return (
                <Box key={app.name}>
                  <Card
                    variant="outlined"
                    sx={{
                      cursor: isAvailable ? "pointer" : "not-allowed",
                      textAlign: "center",
                      height: "80px",
                      width: "120px",
                      border: isSelected
                        ? "2px solid #49A3FF"
                        : "1px solid #ddd",
                      backgroundColor: isAvailable ? "transparent" : "#f5f5f5",
                      opacity: isAvailable ? 1 : 0.5,
                      "&:hover": isAvailable ? { bgcolor: "rgba(0, 0, 0, 0.04)" } : {},
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onClick={() => handleSelectApp(app.name)}
                  >
                    <Box sx={{ opacity: isAvailable ? 1 : 0.5 }}>
                      {app.icon}
                    </Box>
                  </Card>
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 1,
                      color: isSelected 
                        ? "#000000" 
                        : isAvailable 
                        ? "#75777A" 
                        : "#cccccc",
                      fontSize: "14px",
                    }}
                  >
                    {app.name}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          <Box
            sx={{
              mt: 5,
              pt: 2,
              borderTop: 1,
              borderColor: "divider",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            <Button
              onClick={handleCloseModal}
              sx={{
                textTransform: "none",
                width: "80px",
                height: "40px",
                fontSize: "14px",
                color: "#49A3FF",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={!selectedApp}
              onClick={() => handleALMSelectionProceeding()}
              sx={{
                textTransform: "none",
                width: "80px",
                height: "40px",
                fontSize: "14px",
                backgroundColor: "#49A3FF",
              }}
            >
              Next
            </Button>
          </Box>
        </Box>
      </Modal>
    </ThemeProvider>
  );
};

export default SelectAppModal;
